import {
  Box,
  Button as ChakraButton,
  Checkbox,
  Flex,
  Heading as ChakraHeading,
  Image,
  ListItem,
  Spacer,
  Text,
  UnorderedList,
  useBreakpointValue,
} from "@chakra-ui/react";
import AlertBanner from "components/user-settings/AlertBanner";
import { Color } from "theme/old-design-system/styled-components";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import React, { useState } from "react";
import { ExtendedConnectSteps } from "../ConnectIntegrationTypes";
import ProgressSteps from "./steps";

interface StepsContentProps {
  steps: ExtendedConnectSteps[] | undefined;
  imgWidth?: string;
  imgHeight?: string;
  nextButtonText?: string;
  previousButtonText?: string;
  openInNewTab?: boolean;
  showActionsBorder?: boolean;
}

const StepsContent = ({
  steps,
  imgWidth,
  imgHeight,
  nextButtonText = "Continue",
  previousButtonText = "Back",
  openInNewTab = false,
  showActionsBorder = false,
}: StepsContentProps) => {
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const [checked, setChecked] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { colorScheme } = useCustomChakraTheme();
  const forwards = () => setCurrentStep(currentStep + 1);
  const backwards = () => setCurrentStep(currentStep - 1);

  if (steps)
    return (
      <>
        <Box mx="2.5rem" mt={5}>
          <ProgressSteps steps={steps} activeStep={currentStep} size="sm" />
        </Box>

        {steps && currentStep === 0 && (
          <Box px="2.5rem" pt={5}>
            <AlertBanner
              bgColor="rgb(206,230,255)"
              borderColor="rgb(206,230,255)"
              content="Please follow this short step-by-step guide to get started with Instagram successfully."
            />
          </Box>
        )}

        <ChakraHeading
          px="2.5rem"
          size="sm"
          pt={6}
          textAlign={isBaseSize ? "center" : "initial"}
        >
          {steps[currentStep].heading}
        </ChakraHeading>
        <Text px="2.5rem" py={2} textAlign={isBaseSize ? "center" : "initial"}>
          {steps[currentStep].description}
        </Text>
        {steps[currentStep].actionItem && (
          <Box
            p={2}
            mx="2.5rem"
            borderStyle="solid"
            borderWidth="1px"
            borderColor="gray.50"
            borderRadius="md"
            my={2}
          >
            <Checkbox isChecked={checked} onChange={() => setChecked(!checked)}>
              {steps[currentStep].actionItem}
            </Checkbox>
          </Box>
        )}
        <Box px="2.5rem">
          <Text> {steps[currentStep].subPointsHeader}</Text>
          <UnorderedList>
            {steps[currentStep].subPoints?.map((point) => (
              <ListItem key={point}>{point}</ListItem>
            ))}
          </UnorderedList>
        </Box>

        <Text px="2.5rem" py={2} textAlign={isBaseSize ? "center" : "initial"}>
          {steps[currentStep].alternateStep}
        </Text>
        {steps[currentStep].buttonImage && (
          <>
            <Image
              src={`/${steps[currentStep].buttonImage}`}
              width={imgWidth}
              height={imgHeight}
              onClick={
                openInNewTab
                  ? steps[currentStep].openInNewTab
                  : steps[currentStep].onContinue
              }
              cursor="pointer"
              mx="2.5rem"
              mb="1rem"
              alignSelf="end"
            />
          </>
        )}
        <Flex
          mx="2.5rem"
          alignItems="center"
          borderWidth={showActionsBorder ? "1px" : 0}
          borderStyle="solid"
          borderColor="gray.50"
          borderRadius="md"
          p={2}
          justifyContent={currentStep > 0 ? "space-between" : "end"}
        >
          {currentStep > 0 && (
            <ChakraButton
              variant="ghost"
              colorScheme={colorScheme}
              onClick={backwards}
            >
              {previousButtonText}
            </ChakraButton>
          )}
          {!steps[currentStep].buttonImage && (
            <ChakraButton
              disabled={!checked}
              colorScheme={colorScheme}
              onClick={() => {
                forwards();
                setChecked(false);
              }}
            >
              {nextButtonText}
            </ChakraButton>
          )}
        </Flex>
      </>
    );

  return null;
};

export default StepsContent;
